/**
 * Sync Cliente Neto en cotizaciones (misma lógica que pedidos).
 */
import { useEffect, useRef, useCallback, type Dispatch, type SetStateAction } from 'react'
import type { CotizacionData, LineItem } from '../../types/cotizacionFormTypes'
import { calculateTotals } from '../../utils/cotizacionCalculations'
import {
  isClienteNetoActive,
  lineItemSku,
  createClienteNetoAppliedRegistry,
  clearClienteNetoAppliedRegistry,
  markClienteNetoAppliedForLines,
  unmarkClienteNetoAppliedSku,
  getSkusNeedingClienteNetoSync,
  applyClienteNetoSyncToLineItems,
  type ClienteNetoAppliedRegistry
} from '../../utils/clienteNetoOrder'

interface UseClienteNetoCotizacionSyncProps {
  mode: 'view' | 'edit' | 'create'
  formData: CotizacionData
  productDetails: { [key: string]: unknown }
  costoEnvio: string
  setFormData: Dispatch<SetStateAction<CotizacionData>>
}

export interface UseClienteNetoCotizacionSyncReturn {
  markLineItemsCnApplied: (items: LineItem[]) => void
  unmarkLineItemCnApplied: (sku: string) => void
  clearCnAppliedSkus: () => void
}

export function useClienteNetoCotizacionSync({
  mode,
  formData,
  productDetails,
  costoEnvio,
  setFormData
}: UseClienteNetoCotizacionSyncProps): UseClienteNetoCotizacionSyncReturn {
  const registryRef = useRef<ClienteNetoAppliedRegistry>(createClienteNetoAppliedRegistry())
  // edit: precios ya vienen con CN del API.
  // create con líneas precargadas: precios ya vienen con CN (p. ej. datos iniciales).
  const skipInitialBatchRef = useRef(
    mode === 'edit' ||
      (mode === 'create' &&
        isClienteNetoActive(formData.extraDetails) &&
        (formData.line_items?.length ?? 0) > 0)
  )

  const markLineItemsCnApplied = useCallback(
    (items: LineItem[]) => {
      markClienteNetoAppliedForLines(registryRef.current, items, productDetails)
    },
    [productDetails]
  )

  const unmarkLineItemCnApplied = useCallback((sku: string) => {
    unmarkClienteNetoAppliedSku(registryRef.current, sku)
  }, [])

  const clearCnAppliedSkus = useCallback(() => {
    clearClienteNetoAppliedRegistry(registryRef.current)
  }, [])

  const clienteNetoActive = isClienteNetoActive(formData.extraDetails)

  useEffect(() => {
    if (!clienteNetoActive) {
      clearCnAppliedSkus()
      skipInitialBatchRef.current = mode === 'edit'
      return
    }

    const lines = formData.line_items
    if (!lines?.length) return

    if (skipInitialBatchRef.current) {
      markLineItemsCnApplied(lines)
      skipInitialBatchRef.current = false
      return
    }

    const { newSkus, reapplySkus } = getSkusNeedingClienteNetoSync(
      lines,
      productDetails,
      registryRef.current
    )

    if (newSkus.length === 0 && reapplySkus.length === 0) return

    const skusToTouch: string[] = Array.from(new Set([...newSkus, ...reapplySkus]))

    setFormData((prev) => {
      if (!isClienteNetoActive(prev.extraDetails)) return prev
      const prevLines = prev.line_items || []
      const synced = applyClienteNetoSyncToLineItems(
        [...prevLines],
        productDetails,
        newSkus,
        reapplySkus
      )

      const touchedLines = synced.filter((row) => skusToTouch.includes(lineItemSku(row)))
      markClienteNetoAppliedForLines(registryRef.current, touchedLines, productDetails)

      const pricesChanged = synced.some(
        (row, i) => row.price !== prevLines[i]?.price
      )
      if (!pricesChanged) return prev

      const totals = calculateTotals(synced, costoEnvio, productDetails, true)
      return { ...prev, line_items: synced, total: totals.total }
    })
  }, [
    clienteNetoActive,
    formData.line_items,
    productDetails,
    costoEnvio,
    mode,
    setFormData,
    clearCnAppliedSkus,
    markLineItemsCnApplied
  ])

  return { markLineItemsCnApplied, unmarkLineItemCnApplied, clearCnAppliedSkus }
}
